import { assertPaise, paiseToRupees } from "@templeos/shared";
import type { Payment } from "./types.js";

export type PayChoice = "cash" | "upi" | "split";
export class PaymentError extends Error {}

/**
 * Turn the cashier's choice into API payments that add up exactly to the bill. A free bill (free darshan) carries
 * no payments; a split bill needs a cash part strictly between zero and the total, the rest on UPI.
 */
export function buildPayments(total: number, choice: PayChoice, cashPart: number | null, upiRef: string): Payment[] {
  assertPaise(total);
  if (total === 0) return [];
  if (choice === "cash") return [{ mode: "cash", amount: total }];
  if (choice === "upi") return [{ mode: "upi", amount: total, reference: upiRef }];
  if (cashPart === null || !Number.isSafeInteger(cashPart)) throw new PaymentError("Enter the cash part of the split payment.");
  if (cashPart <= 0 || cashPart >= total) throw new PaymentError("The cash part must be more than zero and less than the bill total.");
  return [{ mode: "cash", amount: cashPart }, { mode: "upi", amount: total - cashPart, reference: upiRef }];
}

/** Change to hand back. Null when nothing was tendered; an error when the devotee handed over too little. */
export function changeDue(cashPart: number, tendered: number | null): number | null {
  if (tendered === null) return null;
  assertPaise(tendered);
  if (tendered < cashPart) throw new PaymentError(`Cash received is ₹${paiseToRupees(cashPart - tendered)} short.`);
  return tendered - cashPart;
}

const VPA_RE = /^[a-zA-Z0-9._-]{2,256}@[a-zA-Z][a-zA-Z0-9]{1,63}$/;

/** UPI intent link (NPCI deep-link spec), rendered as a QR for the devotee's UPI app. */
export function upiLink(p: { vpa: string; payee: string; amount: number; ref: string; note?: string }): string {
  if (!VPA_RE.test(p.vpa)) throw new PaymentError("The temple UPI ID is not configured correctly.");
  assertPaise(p.amount);
  if (p.amount <= 0) throw new PaymentError("Nothing to collect on UPI.");
  const q = new URLSearchParams({ pa: p.vpa, pn: p.payee, am: paiseToRupees(p.amount), cu: "INR", tr: p.ref, tn: p.note ?? `Temple counter ${p.ref}` });
  return `upi://pay?${q.toString()}`;
}
